import { InternalMessageResponse } from "features/CenterContent/RoleContent/InternalMessage/schema/responseSchema";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Semantics } from "shared/components/drops/DropsBadge";
import { Flex, Separator, Text } from "shared/components/Spor";
import { NoteFill18Icon } from "shared/icons/NoteIcon";
import { useThemeSemantic } from "shared/theme/themeSemantic";
import { formatDayDateString } from "shared/utils/dateTimeUtils";
import styled from "styled-components";

export type VersionEvent = {
  updatedAt?: string;
  message: string | null;
  user?: string;
  internalMessages?: InternalMessageResponse[];
};

type MessageProps = VersionEvent & {
  semantics: Semantics;
  isInternalMessage?: boolean;
};

type VersionProps = VersionEvent & {
  semantics: Semantics;
  isLastElement: boolean;
};

const Message: FC<MessageProps> = ({
  updatedAt,
  message,
  user,
  semantics,
  isInternalMessage,
  ...rest
}) => {
  const theme = useThemeSemantic(semantics);
  const { t } = useTranslation();
  return (
    <Flex flexDir="column" {...rest}>
      <Flex gap="9px" alignItems="center">
        {isInternalMessage && (
          <Flex className="note" ml="-27px" alignItems="center">
            <NoteFill18Icon $fill={theme.colorSemantic} />
          </Flex>
        )}
        <Text variant="xs" whiteSpace="pre-wrap">
          {message ?? t("Kunne ikke finne distribusjonstekst")}
        </Text>
      </Flex>
      {(updatedAt || user) && (
        <Flex mt="12px" mb="6px" w="100%" justifyContent="space-between">
          <Text variant="xs" color={theme.colorSemanticTextSecondary}>
            {formatDayDateString(updatedAt)}
          </Text>
          <Text variant="xs" color={theme.colorSemanticTextSecondary}>
            {user}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

type MessagesInternalProps = {
  internalMessages: InternalMessageResponse[] | undefined;
  semantics?: Semantics;
  inVersions?: boolean;
};

const Container = styled.div`
  .note {
    margin-left: 0px;
  }
`;

export const MessageInternal: FC<MessagesInternalProps> = ({
  internalMessages,
  semantics = "neutral",
  inVersions = false, // When inside of <Versions/> it is styled differently
}) => {
  const theme = useThemeSemantic(semantics);

  const internalMessageWithoutEvaluationForm = internalMessages?.find(
    (message) => !message.evaluationForm,
  );
  if (!internalMessageWithoutEvaluationForm) return null;

  const message = (
    <Message
      updatedAt={internalMessageWithoutEvaluationForm.updatedAt}
      message={
        internalMessageWithoutEvaluationForm.customTexts[0].texts.NOB
          ?.description ?? ""
      }
      user={
        internalMessageWithoutEvaluationForm.originSystem.user ??
        internalMessageWithoutEvaluationForm.originSystem.name
      }
      semantics={semantics}
      isInternalMessage
    />
  );
  if (inVersions) {
    return (
      <>
        <Separator
          variant="dashed"
          borderRadius="0px"
          borderColor={theme.colorSemanticOutline}
          mx="auto"
        />
        {message}
      </>
    );
  }
  return <Container>{message}</Container>;
};

export const Version: FC<VersionProps> = ({
  updatedAt,
  message,
  isLastElement,
  semantics,
  user,
  internalMessages,
}) => {
  const theme = useThemeSemantic(semantics);
  return (
    <li key={updatedAt}>
      <Flex
        flexDir="column"
        w="100%"
        borderBottom={`${
          isLastElement ? "0px" : "1px"
        } solid ${theme.colorSemanticOutline}`}
        color={theme.colorSemanticTextSecondary}
        gap="6px"
        pb="6px"
      >
        <Message
          message={message}
          updatedAt={updatedAt}
          user={user}
          semantics={semantics}
        />
        <MessageInternal
          internalMessages={internalMessages}
          semantics={semantics}
          inVersions
        />
      </Flex>
    </li>
  );
};
